import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Eaton } from '@brightlayer-ui/icons-mui';

// Domain to IdP password reset URL mapping
// Default: Okta handles all domains except companyABC.tst
// NOTE: Keys must be lowercase because domain is extracted with .toLowerCase()
const DOMAIN_PASSWORD_RESET_MAP = {
    'companyabc.tst': 'http://10.48.49.151:8080/realms/test-realm/login-actions/reset-credentials'
    // All other domains default to Okta
};

const OKTA_PASSWORD_RESET_URL = 'https://id-qa.eaton.com/signin/forgot-password';

const StyledPaper = styled(Paper)(({ theme }) => ({
    width: 500,
    minWidth: 500,
    maxWidth: 500,
    minHeight: 450,
    margin: '100px auto',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: theme.shadows[3]
}));

const StyledForm = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(2)
}));

const EmailFirstPasswordReset = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate email format
            if (!validateEmail(email)) {
                setError('Please enter a valid email address');
                setLoading(false);
                return;
            }

            // Extract domain from email
            const domain = email.toLowerCase().split('@')[1];

            // Map domain to IdP password reset URL (default to Okta for all domains except companyABC.tst)
            const resetUrl = DOMAIN_PASSWORD_RESET_MAP[domain] || OKTA_PASSWORD_RESET_URL;

            // Redirect to IdP's password reset page
            // Keycloak uses 'username' parameter, Okta uses 'email' parameter
            const paramName = domain === 'companyabc.tst' ? 'username' : 'email';
            window.location.href = `${resetUrl}?${paramName}=${encodeURIComponent(email)}`;
        } catch (err) {
            console.error('Password reset error:', err);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && email) {
            handleSubmit(e);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
            }}
        >
            <StyledPaper elevation={3}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Eaton style={{ fontSize: '80px', color: '#007BC1' }} />
                </Box>

                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Reset Password
                </Typography>

                <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 3 }}>
                    Enter your email address and we'll redirect you to reset your password
                </Typography>

                <StyledForm onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        id="email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="user@eaton.com"
                        disabled={loading}
                        autoFocus
                        variant="outlined"
                        margin="normal"
                        required
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={loading || !email}
                        sx={{ mt: 3, mb: 2, py: 1.5, minHeight: 42 }}
                    >
                        {loading ? 'Redirecting...' : 'Continue'}
                    </Button>

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography
                            component="a"
                            href="/"
                            variant="body2"
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Back to Login
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Password Reset Authentication:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            <Typography component="li" variant="body2" color="textSecondary">
                                @companyABC.tst → Test Keycloak
                            </Typography>
                            <Typography component="li" variant="body2" color="textSecondary">
                                All other domains → Okta
                            </Typography>
                        </Box>
                    </Box>
                </StyledForm>
            </StyledPaper>
        </Box>
    );
};

export default EmailFirstPasswordReset;
